import React, { FC, useEffect, useState } from "react";
import sign from "jwt-encode";
import { FileDto, FormBaseType, FormBasicsDto, UserProfileDto } from "@green0meter/api-client";
import { Box, Stack } from "@mui/material";
import { DocumentEditor as OnlyOfficeEditor, IConfig } from "@onlyoffice/document-editor-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { cellExts, diagramExts, pdfExts, slideExts, wordExts } from "./DocumentDetailPage.constants";
import { processFormData, MappedForm } from "./DocumentDetailPage.helpers";

import { Loader, PageTitle } from "@components/common";
import { Button, Card, CardContent, Typography } from "@components/base";
import { useUserStore } from "@store/user";
import { OO_JWT, OO_SERVER } from "@constants";
import {
  useGetAvailableForms,
  useGetFile,
  useGetFolder,
  useGetMultipleFormsDraftData,
  useGetMultipleFormsSections,
} from "@api";
import { CalculationDataItem, useGetCalculationData } from "@api/hooks/calculation";
import AiChat from "@components/main/documentManagement/DocumentDetail/components/AiChat";
import { useUploadFile } from "@api/hooks/ai/UploadFile/useUploadFile";

interface DocumentDetailPageProps {
  fileId: string;
}


const getDocumentType = (fileName: string): string => {
  const extension = fileName.split(".").pop()?.toLowerCase() || "";

  if (wordExts.includes(extension)) return "word";
  if (cellExts.includes(extension)) return "cell";
  if (slideExts.includes(extension)) return "slide";
  if (pdfExts.includes(extension)) return "pdf";
  if (diagramExts.includes(extension)) return "diagram";

  return "word";
};

const generateEditorConfig = (
  file: FileDto,
  publicLink: string,
  user: UserProfileDto,
  sources: FileDto[],
  availableForms: FormBasicsDto[],
  availableData: MappedForm[],
  calculationData?: CalculationDataItem[],
): IConfig => {
  // we should track here lastUpdated and disable cache
  const key = `${file.id}_${+new Date(file.updatedAt || file.createdAt)}`;

  const payload = {
    document: {
      fileType: file.name.split(".").pop() || "",
      key,
      title: file.name,
      url: publicLink,
    },
    documentType: getDocumentType(file.name),
    editorConfig: {
      callbackUrl: `${import.meta.env.VITE_API_BASE_URL}/api/only-office/save-file-callback?fileId=${file.id}&token=${localStorage.getItem("access_token")}`,
      user: {
        id: user.id,
        name: user.firstName + " " + user.lastName,
      },
      plugins: {
        pluginsData: [
          `${import.meta.env.VITE_ONLY_OFFICE_PLUGIN}/config.json?v=${+Date.now()}`,
        ],
        autostart: ["asc.{e913220b-26ad-4ea2-94b9-28705bcbaafe}"],
        options: {
          "asc.{e913220b-26ad-4ea2-94b9-28705bcbaafe}": {
            sources,
            availableForms,
            availableData,
            calculationData,
            environment: import.meta.env.VITE_ENVIRONMENT,
            apiToken: localStorage.getItem("access_token"),
            apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
          },
        },
      },
      customization: {
        logo: {
          visible: false,
        },
        pointerMode: "hand" as const,
        slidePlayerBackground: "#000000",
      },
    },
  };

  // this must be done on BE side, only POC
  const token = sign(payload, OO_JWT);

  // @ts-expect-error some problem with type in library
  return {
    ...payload,
    token,
  };
};

const DocumentDetailPage: FC<DocumentDetailPageProps> = ({ fileId }) => {
  const { user } = useUserStore();
  const { t } = useTranslation();
  const { folder } = useGetFolder(user);
  const [sources, setSources] = useState<FileDto[]>([]);
  const { file, refetchGetFile } = useGetFile(user, fileId!);
  const { availableForms, isSuccessAvailableForms } = useGetAvailableForms(user, [
    FormBaseType.ESRS,
    FormBaseType.Co2,
    FormBaseType.CsobSk,
  ]);
  const [fileUploadCalled, setFileUploadCalled] = useState(false);

  const { calculationData, isSuccessCalculationData } = useGetCalculationData(
    user,
    "8da82033d38510033ef8ca",
  );

  const formIds = availableForms?.map((form) => form.id) || [];
  const { formsDraftData, allFormsDraftDataFetched } = useGetMultipleFormsDraftData(
    user,
    formIds,
    isSuccessAvailableForms,
  );
  const { formsSections, allFormsSectionsFetched } = useGetMultipleFormsSections(
    user,
    formIds,
  );

  const uploadFileMutation = useUploadFile();

  useEffect(() => {
    if (file && !fileUploadCalled) {
      uploadFileMutation.mutate({
        file,
      });
      setFileUploadCalled(true);
    }
  }, [file, fileUploadCalled]);

  const navigate = useNavigate();

  const [publicLink, setPublicLink] = useState<string | null>(null);
  const [availableData, setAvailableData] = useState<MappedForm[]>([]);

  useEffect(() => {
    refetchGetFile();
    if (
      file &&
      folder &&
      isSuccessAvailableForms &&
      allFormsDraftDataFetched &&
      allFormsSectionsFetched
    ) {
      const link = `${import.meta.env.VITE_API_BASE_URL}/api/only-office/get-file?fileId=${file.id}&token=${localStorage.getItem("access_token")}`;

      setPublicLink(link);
      setSources(folder.files);

      const mappedData: MappedForm[] = [];

      formsDraftData.forEach((formDraftQuery, formIndex) => {
        if (
          !formDraftQuery.data ||
          !formsSections[formIndex]?.data ||
          !availableForms
        )
          return;

        const draftData = formDraftQuery.data;
        const sections = formsSections[formIndex].data;
        const form = availableForms[formIndex];

        if (!form || !sections) return;

        const processedForm = processFormData(draftData, sections, form.id, form.name);

        if (processedForm) {
          mappedData.push(processedForm);
        }
      });

      setAvailableData(mappedData);
    }
  }, [
    file,
    folder,
    isSuccessAvailableForms,
    allFormsDraftDataFetched,
    allFormsSectionsFetched,
    isSuccessCalculationData,
  ]);

  if (!file || !user) {
    return <Loader />;
  }

  return (
    <Card>
      <CardContent>
        <Stack mb={6} height="100%" flexDirection="column">
          <Box
            display="flex"
            flexDirection="row"
            alignItems={"center"}
            flexGrow={1}
            mb={3}
          >
            <Box>
              <Button
                variant="outlined"
                sx={{ mr: 2 }}
                onClick={() => navigate("/document-management-hub")}
                startIcon={<i className="fe fe-arrow-left" />}
              >
                {t("common.back")}
              </Button>
            </Box>
            <Box>
              <PageTitle title={file.name} />
              <Typography variant="subtitle1">{file.name}</Typography>
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" height="100vh">
            {file && publicLink ? (
              <>
                <Box height="70%">
                  <OnlyOfficeEditor
                    id={"edi"}
                    height="100%"
                    width="100%"
                    documentServerUrl={OO_SERVER}
                    config={generateEditorConfig(
                      file,
                      publicLink,
                      user,
                      sources,
                      availableForms || [],
                      availableData,
                      calculationData,
                    )}
                  />
                </Box>
                <Box height="30%">
                  <AiChat file={file} />
                </Box>
              </>
            ) : (
              <Loader />
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DocumentDetailPage;
